import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { fleetPeriodUsageSchema } from "./accounts.js";
import { fleetPolicySchema, fleetVehicleRequestStatusSchema } from "./enums.js";
import { fleetPolicyLayerFieldsSchema, fleetRemainingSchema } from "./policies.js";
/**
 * Filo PORTALI (ADR 0017 §17): filo carisinin kendi kullanıcısı (`FLEET_PORTAL_USER`, CARİ kapsamlı atama). Cari kimliği
 * YALNIZCA kullanıcının atamasından türetilir (`portalCounterpartyIds`); personel (kiracı / şirket kapsamlı) bu uçlarda 403
 * alır. Portal kendi filosunun verisini gösterir: plaka ve sürücü adı AÇIKTIR (filonun kendi kurumsal verisi).
 * Birden çok cariye atanmış kullanıcı `counterpartyId` ile seçer; tek cari varsa zorunlu değildir.
 */
export const fleetPortalScopeQuerySchema = z.object({
    counterpartyId: uuidSchema.optional().describe("Birden çok filoya erişimi olan kullanıcı için"),
});
export const fleetPortalAccountSchema = z.object({
    counterpartyId: uuidSchema,
    counterpartyCode: z.string(),
    counterpartyName: z.string(),
});
export const fleetPortalAccountListSchema = z.object({ items: z.array(fleetPortalAccountSchema) });
const namedRefSchema = z.object({ id: uuidSchema, name: z.string() });
/** Portal kural görünümü: motor şeması + izinli ürün / istasyonların ADLARI (kimliklerle aynı sırada). */
export const fleetPortalPolicySchema = fleetPolicySchema.extend({
    allowedProducts: z
        .array(namedRefSchema)
        .nullable()
        .describe("`allowedProductIds` ile aynı ürünler, adıyla (null = kısıt yok)"),
    allowedStations: z
        .array(namedRefSchema)
        .nullable()
        .describe("`allowedStationIds` ile aynı istasyonlar, adıyla (null = kısıt yok)"),
});
export const fleetPortalVehicleSchema = z.object({
    vehicleId: uuidSchema,
    plate: z.string(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    fuelProductName: z.string().nullable(),
    status: z.enum(["ACTIVE", "INACTIVE"]),
    policy: fleetPortalPolicySchema.describe("Etkin kural (cari varsayılanı + araç geçersiz kılması)"),
    usage: fleetPeriodUsageSchema,
    remaining: fleetRemainingSchema,
    lastOdometerKm: z.number().int().nullable(),
});
export const fleetPortalVehicleListSchema = z.object({ items: z.array(fleetPortalVehicleSchema) });
export const fleetPortalSummarySchema = z.object({
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    billingCycle: z.string(),
    credit: z.array(z.object({
        companyName: z.string(),
        creditLimit: moneySchema.nullable(),
        balance: moneySchema.describe("Toplam risk (açık bakiye + çek + sipariş)"),
        available: moneySchema.nullable(),
    })),
    usage: fleetPeriodUsageSchema,
    vehicles: z.array(fleetPortalVehicleSchema),
});
export const fleetPortalTransactionSchema = z.object({
    id: uuidSchema,
    occurredAt: timestampSchema,
    localDay: localDateSchema,
    stationName: z.string(),
    vehicleId: uuidSchema.nullable(),
    plate: z.string().nullable(),
    driverName: z.string().nullable(),
    productName: z.string().nullable(),
    quantity: quantitySchema,
    amount: moneySchema,
    odometerKm: z.number().int().nullable(),
    kind: z.enum(["SALE", "REVERSAL"]).describe("REVERSAL: iptal / iade karşı kaydı (negatif)"),
    receiptNo: z.string().nullable().describe("ÖKC fişi: seri / Z / fiş"),
});
export const fleetPortalTransactionPageSchema = pageOf(fleetPortalTransactionSchema);
export const listFleetPortalTransactionsQuerySchema = pageQuerySchema.extend({
    counterpartyId: uuidSchema.optional(),
    vehicleId: uuidSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const fleetPortalInvoiceSchema = z.object({
    id: uuidSchema,
    companyName: z.string(),
    invoiceNo: z.string().nullable(),
    invoiceType: z.string(),
    basis: z.enum(["STANDARD", "FISCAL_RECEIPTS"]),
    issueDate: localDateSchema,
    dueDate: localDateSchema,
    grossAmount: moneySchema,
    payableAmount: moneySchema,
    status: z.enum(["ISSUED", "CANCELLED"]),
});
export const fleetPortalInvoicePageSchema = pageOf(fleetPortalInvoiceSchema);
export const listFleetPortalInvoicesQuerySchema = pageQuerySchema.extend({
    counterpartyId: uuidSchema.optional(),
});
/**
 * Araç talebinde seçilecek yakıt: carinin bağlı olduğu şirketlerin istasyonlarındaki ETKİN tanklarda bulunan akaryakıt /
 * LPG ürünleri; o şirketlerde hiç tank tanımlı değilse katalogdaki etkin akaryakıt / LPG ürünleri (katalog kiracı
 * düzeyindedir).
 */
export const fleetPortalProductSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    productType: z.enum(["FUEL", "LPG"]),
});
export const fleetPortalProductListSchema = z.object({ items: z.array(fleetPortalProductSchema) });
// ── Araç ekleme talebi ──────────────────────────────────────────────────────
export const fleetVehicleRequestSchema = z.object({
    id: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    plate: z.string().describe("KVKK (personel tarafında maskeli olabilir)"),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    fuelProductId: uuidSchema.nullable(),
    note: z.string().nullable(),
    status: fleetVehicleRequestStatusSchema,
    decidedAt: timestampSchema.nullable(),
    decisionNote: z.string().nullable(),
    vehicleId: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
    personalDataMasked: z.boolean(),
});
export const fleetVehicleRequestPageSchema = pageOf(fleetVehicleRequestSchema);
export const createFleetVehicleRequestSchema = z.object({
    counterpartyId: uuidSchema.optional().describe("Birden çok filoya erişimi olan kullanıcı için"),
    plate: z.string().trim().min(2).max(20),
    brand: z.string().trim().min(1).max(60).nullable().default(null),
    model: z.string().trim().min(1).max(60).nullable().default(null),
    fuelProductId: uuidSchema.nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
});
export const listFleetVehicleRequestsQuerySchema = pageQuerySchema.extend({
    counterpartyId: uuidSchema.optional(),
    status: fleetVehicleRequestStatusSchema.optional(),
});
/** Personel onayı: araç customers modülünün `createVehicleInTx`'iyle açılır; isteğe bağlı ilk araç kuralı yazılır. */
export const approveFleetVehicleRequestSchema = z.object({
    version: z.number().int().positive(),
    fuelProductId: uuidSchema.nullable().optional().describe("Verilmezse talepteki ürün"),
    decisionNote: z.string().trim().max(500).nullable().default(null),
    policy: fleetPolicyLayerFieldsSchema.nullable().default(null).describe("Araca özel ilk kural katmanı"),
});
export const rejectFleetVehicleRequestSchema = z.object({
    version: z.number().int().positive(),
    decisionNote: z.string().trim().min(3).max(500),
});
//# sourceMappingURL=portal.js.map